// src/services/companyJobFeedService.js
//
// A company configuring its OWN job feed, against the group-scoped
// `/company/job-feed` surface.
//
// Deliberately not part of employerFeedService, which talks to
// `/admin/employer-feeds` authorized with `adminTokenBackup`. That is correct
// for the managed-account case (an admin acting-as a RxRooster-operated
// account, where localStorage.authToken is a GROUP token the admin routes
// reject) and it must keep working. It is exactly wrong here: a real company
// has no admin token, and the whole point of this surface is that it does not
// need one.
//
// Everything goes through apiClient, like every other authed /company/* call,
// so the token handling and error-envelope unwrapping are the shared ones.
//
// groupId is never sent. The backend derives it from the JWT and answers 403
// to a body naming a different group, so passing one can only turn a working
// call into a broken one.
import apiClient from './apiClient';
import { notifyJourneyChanged } from './companyJourneyService';
import { notifyListingsCountChanged } from './listingsCountSignal';

// Read dedupe for list(). Two components read the feed list on a company page
// load, and StrictMode mounts each twice in dev, so the same small config read
// went out 4 times (2 in production). api.js's DEDUPE_RULES cannot help here:
// that layer wraps the shared axios instance, and this module goes through
// apiClient on purpose (see the note above).
//
// Short window on purpose. It exists to collapse a burst of mounts, not to hold
// config across a session, so a change made in another tab shows up on the next
// read rather than minutes later.
const LIST_CACHE_MS = 5_000;

let listInflight = null;
let listCache = null; // { value, expiresAt }
const changeListeners = new Set();

/**
 * Let status readers refresh immediately after a successful mutation. The
 * settings accordion, listings callout and feed editor are separate component
 * trees; invalidating the HTTP cache alone does not re-run an already-mounted
 * hook in either of the first two.
 */
export const subscribeToJobFeedChanges = (listener) => {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
};

const notifyJobFeedChanged = () => {
  for (const listener of changeListeners) {
    try {
      listener();
    } catch {
      // One decorative status reader must never fail the mutation that changed it.
    }
  }
};

/**
 * Drop the cached list. Every mutation in this service calls this, because a
 * caller that creates a feed and immediately re-lists must not be told its own
 * feed does not exist.
 */
const invalidateList = () => {
  listCache = null;
  listInflight = null;
};

/**
 * An admin changes this feed from a different browser, so that mutation cannot
 * pass through `mutating()` above. The notification socket calls this public
 * invalidation hook when the review result arrives: cached readers then fetch
 * the newly trusted row instead of holding the pre-approval snapshot for the
 * rest of its cache window.
 */
export const notifyExternalJobFeedChange = () => {
  invalidateList();
  notifyJobFeedChanged();
};

/** How long a feed removal's background role teardown usually takes. */
const REMOVAL_SETTLE_MS = 15000;

/** Run a mutation, then drop the cache and wake mounted status readers. */
const mutating = (run) => {
  invalidateList();
  return Promise.resolve()
    .then(run)
    .then(
      (value) => {
        invalidateList();
        notifyJobFeedChanged();
        // AND THE TOPBAR, which counts published listings and is drawn from a
        // read CompanyContext takes once per session. Every write on this
        // service happens on Settings, a different route from the badge, so
        // none of them can reach the listings roster's own `onListingsChanged`.
        // The sharpest case is `remove`, whose success toast says "Its imported
        // roles were unpublished and soft-deleted" while the badge went on
        // showing the old number; `pull` is the other way round and imports
        // them. Announced from the chokepoint rather than per verb so a new
        // write here gets it without anyone remembering to.
        notifyListingsCountChanged();
        return value;
      },
      (error) => {
        invalidateList();
        throw error;
      },
    );
};

export const companyJobFeedService = {
  /** This company's feeds. The backend scopes the query; there is no filter to pass. */
  list: () => {
    if (listCache && listCache.expiresAt > Date.now()) {
      return Promise.resolve(listCache.value);
    }
    if (listInflight) return listInflight;

    listInflight = apiClient
      .get('/company/job-feed')
      .then((value) => {
        listCache = { value, expiresAt: Date.now() + LIST_CACHE_MS };
        listInflight = null;
        return value;
      })
      .catch((err) => {
        // A failure is never cached: the next read retries.
        listInflight = null;
        throw err;
      });

    return listInflight;
  },

  /**
   * Is there a usable job feed at this URL, and in what format? Writes nothing.
   *
   * Answers a verdict rather than throwing for an ordinary "no": a 404 or a
   * dead host is a legitimate answer to this question. It still rejects on a
   * transport failure, which the caller shows as an unknown rather than a no.
   */
  probe: (payload) => apiClient.post('/company/job-feed/probe', payload),

  /**
   * Preview probe sample rows before create has produced a feed id. Like the
   * saved-feed preview below, this fetches nothing and writes nothing.
   */
  previewDraft: ({ rows, fieldMapping, providerTypeMapping, company }) =>
    apiClient.post('/company/job-feed/preview', {
      rows: rows ?? [],
      fieldMapping: fieldMapping ?? {},
      ...(providerTypeMapping !== undefined ? { providerTypeMapping } : {}),
      company,
    }),

  create: (payload) =>
    mutating(() =>
      apiClient.post('/company/job-feed', payload).then((feed) => {
        // A connected feed is a side quest on the activation board, derived
        // from the feed row rather than stamped, so the board only needs to be
        // told to look again. Inside the post's then rather than around
        // `mutating` so a rejected create announces nothing. Guarded so a
        // broken listener cannot turn a connected feed into a rejected create.
        try {
          notifyJourneyChanged();
        } catch {
          /* the journey board is decoration; it must never fail the create it followed */
        }
        return feed;
      }),
    ),

  update: (id, patch) =>
    mutating(() => apiClient.patch(`/company/job-feed/${id}`, patch)),

  /**
   * Parses the live feed and reports what would be imported. Writes nothing.
   *
   * `draft` is the form as it stands, not the saved feed. Sending nothing tests
   * the stored configuration, which is what this used to do unconditionally and
   * is why the button looked broken: the probe above it reported on the URL just
   * typed while the test reported on the URL still saved, and the two verdicts
   * appeared together with no sign they were about different addresses.
   */
  dryRun: (id, draft) =>
    apiClient.post(`/company/job-feed/${id}/dry-run`, draft ?? {}),

  /** Whole-feed review before the first feed document exists. Writes nothing. */
  dryRunDraft: (draft) =>
    apiClient.post('/company/job-feed/dry-run', draft ?? {}),

  /**
   * Fetch the feed for real, now, instead of waiting for the six-hourly sweep.
   *
   * The ONE write on this service that reaches outside RxRooster, and the only
   * one whose effect is listings rather than configuration. `mutating` because
   * a successful pull rewrites `lastFetchAt`, `lastGoodRowCount` and the
   * failure streak, and the card reads all three: a cached list would leave the
   * reader looking at "Last pulled: Not yet" over a pull that had just landed,
   * which is the one sentence this button exists to change.
   *
   * `confirmReplace` is omitted rather than sent as false. The server only asks
   * for it on a feed an admin switched to replace, where a pull closes every
   * live role the feed does not carry, and a caller that has not been asked
   * must not be able to answer.
   */
  pull: (id, { confirmReplace } = {}) =>
    mutating(() =>
      apiClient.post(
        `/company/job-feed/${id}/pull`,
        confirmReplace ? { confirmReplace: true } : {},
      ),
    ),

  /**
   * What these exact rows would become under this exact mapping.
   *
   * A READ, so it deliberately does not go through `mutating()`: it fetches
   * nothing, enriches nothing and persists nothing, and dropping the list cache
   * on every keystroke of an unsaved mapping would be the opposite of correct.
   *
   * `rows` are the raw records the dry run already returned as `sampleRaw`.
   * Sending them back rather than re-fetching is the whole point of the split:
   * the mapping modal repaints on every change, and a partner's feed must not
   * be pulled once per change.
   */
  preview: (id, { rows, fieldMapping, providerTypeMapping } = {}) =>
    apiClient.post(`/company/job-feed/${id}/preview`, {
      rows: rows ?? [],
      fieldMapping: fieldMapping ?? {},
      ...(providerTypeMapping !== undefined ? { providerTypeMapping } : {}),
    }),

  /**
   * Disconnect. A soft stop server-side: the feed leaves this company's surface
   * and the ops queue, but the document survives because `recentRuns` is the
   * only record of what it did and `reviewRequests` the only record of what we
   * asked about it.
   *
   * `mutating` is load-bearing rather than housekeeping: without dropping the
   * 30s list cache the removed feed keeps rendering for half a minute after the
   * company removed it.
   *
   * `?confirm=remove` IS NOT OPTIONAL. This verb used to pause a feed
   * reversibly; it now disconnects permanently and scrubs the stored
   * credential, and the controller refuses any caller that does not carry the
   * flag so that a frontend still showing the old "Pause feed..." button cannot
   * make an irreversible change behind a dialog that promises otherwise. A
   * DELETE without it is answered 400, which is a client that never removed
   * anything while telling the company it had.
   *
   * A query flag rather than a body because DELETE bodies are not carried
   * reliably by every proxy, and `apiClient.delete` takes a path only, so it
   * goes in the string.
   */
  remove: (id) =>
    mutating(() => apiClient.delete(`/company/job-feed/${id}?confirm=remove`)).then(
      (value) => {
        // The server answers once the feed is disconnected and unpublishes its
        // roles in the background, so the count moves again shortly after.
        if (value?.removal === 'in_progress') {
          setTimeout(notifyListingsCountChanged, REMOVAL_SETTLE_MS);
        }
        return value;
      },
    ),

  /**
   * Point the feed at a different address. The only way a connected feed's
   * connection changes: PATCH refuses those fields outright.
   *
   * Costs a review every time, so the caller must have confirmed it. The server
   * probes the new address before writing anything, so a rejection here means
   * nothing changed and the old feed is still live.
   */
  reconfigure: (id, connection) =>
    mutating(() => apiClient.post(`/company/job-feed/${id}/reconfigure`, connection)),

  /**
   * The answer to a change we asked for: closes the open request and puts the
   * feed back in the review queue. `reply` is optional and is the company's own
   * words to us, not a field of the configuration.
   *
   * `mutating` like every other write here, and here it is load-bearing rather
   * than housekeeping: the server flips reviewStatus back to 'pending', and the
   * card reads that field to decide whether to say "Changes requested". A
   * cached list would leave the ask on screen over a request that is already
   * closed, which is the one thing this whole flow exists to stop.
   *
   * The key is omitted rather than sent as undefined when there is no reply:
   * `JSON.stringify` drops it either way, and saying so here means nobody has
   * to know that to read this.
   */
  resubmit: (id, { reply } = {}) =>
    mutating(() =>
      apiClient.post(`/company/job-feed/${id}/resubmit`, reply ? { reply } : {}),
    ),

  /**
   * Attach one file of instructions: a feed spec, a sample export, a screenshot
   * of the ATS export screen. Replaces whatever was there before.
   *
   * Goes through apiClient.upload, which omits the JSON content-type so the
   * browser sets the multipart boundary itself.
   */
  uploadAttachment: (id, file) => {
    const body = new FormData();
    body.append('file', file);
    return mutating(() =>
      apiClient.upload(`/company/job-feed/${id}/attachment`, body),
    );
  },
};

export default companyJobFeedService;
